import React, { useState, forwardRef } from "react";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Pagination from "@mui/material/Pagination";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation, Trans } from "@i18n";
import Text from "../Text/Text";

interface PagerComponentProps {
  pageSize: number;
  currentPage: number;
  totalItemCount: number;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  totalPages: number;
  allowPagination: boolean;
  selectedItems?: number;
  currentLoadedCount?: number;
  infiniteScroll?: boolean;
}

const PagerComponent = forwardRef<HTMLDivElement, PagerComponentProps>(
  (
    {
      pageSize,
      currentPage,
      totalItemCount,
      setCurrentPage,
      setPageSize,
      totalPages,
      allowPagination,
      selectedItems = 0,
      currentLoadedCount,
      infiniteScroll = false,
    },
    _ref,
  ) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const { t } = useTranslation();

    const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
      setCurrentPage(newPage);
    };

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleMenuItemClick = (value: number) => {
      setAnchorEl(null);
      if (value > 0) setPageSize(value);
    };

    const customFooter = () => (
      <div className="flex w-full justify-between text-sm items-center">
        <div>
          <Text>
            <Trans i18nKey="items_selected" values={{ count: selectedItems }} />
          </Text>
        </div>
        <div>
          {infiniteScroll && currentLoadedCount !== undefined && totalItemCount ? (
            <span className="mx-1">
              <Text>
                <Trans i18nKey="showing_of_items" values={{ current: currentLoadedCount, total: totalItemCount }}>
                  Showing {{ current: currentLoadedCount }} of {{ total: totalItemCount }} items
                </Trans>
              </Text>
            </span>
          ) : (
            <span className="mx-1">
              <Text>
                <Trans i18nKey="total_items" values={{ total: totalItemCount }}>
                  {{ total: totalItemCount }} items
                </Trans>
              </Text>
            </span>
          )}
        </div>
      </div>
    );

    const pageSizeOptions: (number | "All")[] = ["All", 5, 10, 15, 20];

    return (
      <>
        {allowPagination && (
          <div className="flex flex-row w-full items-center mt-3 mb-5 text-sm ">
            <Pagination
              count={totalPages}
              showFirstButton
              page={currentPage}
              showLastButton
              onChange={handlePageChange}
            />
            <Button
              sx={{
                border: "1px solid #767676",
                justifyContent: "space-between",
                color: "black",
                borderRadius: "0",
              }}
              className="ms-2"
              aria-controls={open ? "page-size-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={handleClick}
            >
              <div className="flex items-center justify-between w-full">
                <span>{pageSize === totalItemCount ? t("All") : pageSize}</span>
                <ExpandMoreIcon style={{ color: "gray" }} />
              </div>
            </Button>
            <Menu
              id="page-size-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              slotProps={{
                list: {
                  style: {
                    paddingTop: "0.625rem",
                    paddingBottom: "0.625rem",
                  },
                },
              }}
            >
              {pageSizeOptions.map((size: number | "All") => {
                const value = size === "All" ? totalItemCount : size;
                const isSelected = size === "All" ? pageSize === totalItemCount : pageSize === size;
                return (
                  <MenuItem key={size} onClick={() => handleMenuItemClick(value as number)} selected={isSelected}>
                    {size === "All" ? <Text>{t("All")}</Text> : <Text>{size}</Text>}
                  </MenuItem>
                );
              })}
            </Menu>
            <span className="ms-2 me-2" data-testid="itemsPerPageText">
              {<Text>{t("Items per page")}</Text>}
            </span>

            <div className="flex-1" />
            <div className="contents">
              <div className="flex items-center ms-6" data-testid="pageItemsText">
                <Text>
                  {/* `pagination_status` carries no <0> tags, so <Trans> renders the interpolated
                      catalog string and never the child markup - the wrapper spans were dead and
                      only existed to host the interpolation objects. Same childless shape as the
                      other <Trans> calls in this file. */}
                  <Trans
                    i18nKey="pagination_status"
                    values={{ current: currentPage, count: totalPages, total: totalItemCount }}
                  />
                </Text>
              </div>
            </div>
          </div>
        )}
        {!allowPagination && customFooter()}
      </>
    );
  },
);

PagerComponent.displayName = "PagerComponent";

export default PagerComponent;
